import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  Inject,
} from '@angular/core';
import {User} from '@drl/core/auth/models/user.model';
import {PermissionKey} from '@drl/core/auth/permission-key.enum';
import {IpsTranslateService} from '@drl/core/localization/ips-translate.service';
import {NameId} from '@drl/core/models';
import {UserSessionStoreService} from '@drl/core/store/user-session-store.service';
import {CMSFacadeService} from '@drl/shared/components/cms-list/cms-facade.service';
import {ContentModel} from '@drl/shared/components/cms-list/models';
import {TranslateService} from '@ngx-translate/core';
import {forkJoin, Subscription} from 'rxjs';
import {tap} from 'rxjs/operators';

import {contentType, FileType} from './enums/content-type.enum';
import {ModelEnum} from './enums/model.enum';
import {APPLICATION_STORE} from '@drl/core/store/store.interface';
import {StorageService} from 'ngx-webstorage-service';

const defaultLimit = 10;

@Component({
  selector: 'drl-cms-list',
  templateUrl: './cms-list.component.html',
  styleUrls: ['./cms-list.component.scss'],
})
export class CmsListComponent implements OnInit, OnDestroy {
  constructor(
    private readonly cmsFacade: CMSFacadeService,
    private readonly store: UserSessionStoreService,
    protected readonly translateSvc: IpsTranslateService,
    @Inject(APPLICATION_STORE) private readonly storeService: StorageService,
  ) {
    this.translate = this.translateSvc.translate;
  }

  translate: TranslateService;

  @Input()
  model: number;

  @Input()
  allowSelect = false;

  @Input()
  isCareChat = false;

  @Output()
  onSelect: EventEmitter<ContentModel> = new EventEmitter();

  @Output()
  onCancel: EventEmitter<void> = new EventEmitter();

  nameFilter: string;
  subcategory1Filter: string[] = [];
  subcategory2Filter: string[] = [];
  subcategory3Filter: string[] = [];
  categoryFilter: string[] = [];
  conditionFilter: string[] = [];
  typeFilter: string[] = [];
  ownUser: User;

  permissionKeys = PermissionKey;
  subcategories1: NameId[] = [];
  subcategories2: NameId[] = [];
  subcategories3: NameId[] = [];
  categories: NameId[] = [];
  conditions: NameId[] = [];
  content: ContentModel[] = [];
  contents: ContentModel[] = [];
  types = contentType(this.translateSvc.translate);
  loading = false;
  isListLoading = false;
  modelTypes = ModelEnum;
  fileTypes = FileType;

  publishedGalleriesLbl: string;
  publishedPostsLbl: string;

  protected _subscriptions: Subscription[] = [];

  ngOnDestroy() {
    this.clearAllSubscriptions();
  }

  getTranslations() {
    return this.translate
      .get(['publishedGalleriesLbl', 'publishedPOstsLbl'])
      .pipe(
        tap(res => {
          this.publishedGalleriesLbl = res.publishedGalleriesLbl;
          this.publishedPostsLbl = res.publishedPOstsLbl;
        }),
      );
  }

  clearAllSubscriptions() {
    this._subscriptions.forEach(subscription => {
      if (subscription) {
        subscription.unsubscribe();
      }
    });
    this._subscriptions = [];
  }

  ngOnInit() {
    this.ownUser = this.store.getUser();
    this.loading = true;

    const [condIndex, catIndex, subCat1Index, subCat2Index, subCat3Index] = [
      0, 1, 2, 3, 4,
    ];
    this.conditions = [];
    this.categories = [];
    this.subcategories1 = [];
    this.subcategories2 = [];
    this.subcategories3 = [];

    this._subscriptions.push(
      forkJoin([
        this.cmsFacade.getConditions(),
        this.cmsFacade.getCategories(),
        this.cmsFacade.getSubCategories1(),
        this.cmsFacade.getSubCategories2(),
        this.cmsFacade.getSubCategories3(),
        this.getTranslations(),
      ]).subscribe(
        responses => {
          responses[condIndex].forEach(row => {
            this.conditions.push(row);
          });

          responses[catIndex].forEach(row => {
            this.categories.push(row);
          });

          responses[subCat1Index].forEach(row => {
            this.subcategories1.push(row);
          });

          responses[subCat2Index].forEach(row => {
            this.subcategories2.push(row);
          });

          responses[subCat3Index].forEach(row => {
            this.subcategories3.push(row);
          });

          this.loading = false;
          this.refresh();
        },
        () => {
          // AL-1182: A temporary fix
          if (
            this.storeService.get(
              `cms-list-error-${this.store.getUser().username}`,
            ) !== 'true'
          ) {
            this.storeService.set(
              `cms-list-error-${this.store.getUser().username}`,
              'true',
            );
            // sonarignore:start
            setTimeout(() => {
              window.location.reload();
            }, 3000);
            // sonarignore:end
          }
        },
      ),
    );
  }

  refresh() {
    this.contents = [];
    if (this.model === this.modelTypes.CONTENT) {
      this.getContents();
    } else {
      this.getGalleries();
    }
  }

  fetchMoreData() {
    if (this.model === this.modelTypes.CONTENT) {
      this.getContents();
    } else {
      this.getGalleries();
    }
  }

  getContents() {
    if (this.isListLoading) {
      return;
    }
    this.isListLoading = true;
    this._subscriptions.push(
      this.cmsFacade
        .getContents(
          defaultLimit,
          this.contents.length,
          this.nameFilter,
          this.conditionFilter,
          this.categoryFilter,
          this.subcategory1Filter,
          this.subcategory2Filter,
          this.subcategory3Filter,
          this.typeFilter,
        )
        .subscribe(
          res => {
            this.contents.push(...res);
          },
          null,
          () => {
            this.isListLoading = false;
          },
        ),
    );
  }

  getGalleries() {
    if (this.isListLoading) {
      return;
    }
    this.isListLoading = true;

    this._subscriptions.push(
      this.cmsFacade
        .getGalleries(
          defaultLimit,
          this.contents.length,
          this.nameFilter,
          this.conditionFilter,
          this.categoryFilter,
          this.subcategory1Filter,
          this.subcategory2Filter,
          this.subcategory3Filter,
        )
        .subscribe(
          res => {
            this.contents.push(...res);
          },
          null,
          () => {
            this.isListLoading = false;
          },
        ),
    );
  }

  onNameQueryChange(query: string) {
    this.nameFilter = query;
    this.refresh();
  }

  onCategoryChange(query: string[]) {
    this.categoryFilter = query;
    this.refresh();
  }

  onTypeChange(query: string[]) {
    this.typeFilter = query;
    this.refresh();
  }

  onConditionChange(query: string[]) {
    this.conditionFilter = query;
    this.refresh();
  }

  onSubCategory1Change(query: string[]) {
    this.subcategory1Filter = query;
    this.refresh();
  }
  onSubCategory2Change(query: string[]) {
    this.subcategory2Filter = query;
    this.refresh();
  }
  onSubCategory3Change(query: string[]) {
    this.subcategory3Filter = query;
    this.refresh();
  }

  selectContent(content: ContentModel) {
    this.onSelect.emit(content);
  }
}
